import { useState, useRef, useEffect } from "react";
import { LockSimple, Check, DotsSixVertical, Warning } from "@phosphor-icons/react";
import {
  SLOTS_PER_DAY, taskColor, WORK_MODES, parseWall, minutesFromMidnight, durationMin,
  dateKey, slotToDate, fmtHour, fmtRange, isToday,
} from "@/components/schedule/utils";

function DayColumn({ day, slotH, events, blocks, modes, dragItem, setDragItem, onSlotClick, onDropTask, onMoveBlock, onBlockClick }) {
  const [hover, setHover] = useState(null);
  const key = dateKey(day);
  const dayEvents = events.filter((e) => dateKey(parseWall(e.start)) === key);
  const dayBlocks = blocks.filter((b) => dateKey(parseWall(b.start)) === key);
  const dayModes = modes.filter((m) => dateKey(parseWall(m.start)) === key);
  const height = SLOTS_PER_DAY * slotH;

  const pos = (iso, endIso) => {
    const top = (minutesFromMidnight(parseWall(iso)) / 30) * slotH;
    const h = Math.max(slotH - 3, (durationMin(iso, endIso) / 30) * slotH - 3);
    return { top, height: h };
  };

  const handleDrop = (slotIndex) => {
    setHover(null);
    const start = slotToDate(day, slotIndex);
    if (!dragItem) return;
    if (dragItem.type === "task") onDropTask(dragItem.id, start);
    else if (dragItem.type === "block") onMoveBlock(dragItem.id, start);
    setDragItem(null);
  };

  return (
    <div className="relative flex-1 min-w-[120px] border-l border-border" style={{ height }}>
      {/* work-mode tint + rail */}
      {dayModes.map((m) => {
        const cfg = WORK_MODES[m.mode] || WORK_MODES.office;
        const { top, height: h } = pos(m.start, m.end);
        return (
          <div key={m.id} className="pointer-events-none">
            <div className={`absolute left-0 right-0 ${cfg.tint}`} style={{ top, height: h }} />
            <div className={`absolute left-0 w-1.5 rounded-full ${cfg.band}`} style={{ top: top + 2, height: h - 4 }} />
          </div>
        );
      })}

      {/* slot cells */}
      {Array.from({ length: SLOTS_PER_DAY }).map((_, i) => (
        <div
          key={i}
          data-testid={`slot-${key}-${i}`}
          onClick={() => onSlotClick(slotToDate(day, i))}
          onDragOver={(e) => { e.preventDefault(); if (hover !== i) setHover(i); }}
          onDragLeave={() => setHover((h) => (h === i ? null : h))}
          onDrop={() => handleDrop(i)}
          className={`relative z-10 cursor-pointer transition-colors ${i % 2 === 0 ? "border-t border-slate-200" : "border-t border-slate-100"} ${hover === i ? "bg-indigo-50" : "hover:bg-slate-50/70"}`}
          style={{ height: slotH }}
        />
      ))}

      {/* meetings (read-only) */}
      {dayEvents.map((e) => {
        const { top, height: h } = pos(e.start, e.end);
        return (
          <div
            key={e.id}
            data-testid={`grid-event-${e.id}`}
            className={`absolute left-2 right-1.5 z-20 pointer-events-none rounded-lg border px-2 py-1 overflow-hidden text-left ${e.is_conflict ? "bg-amber-50 border-amber-300" : "bg-slate-100 border-slate-300"}`}
            style={{ top, height: h }}
            title={`${e.title} · ${fmtRange(e.start, e.end)}`}
          >
            <div className="flex items-center gap-1 text-[11px] font-semibold text-slate-700 truncate">
              {e.is_conflict ? <Warning weight="fill" size={11} className="text-amber-500 shrink-0" /> : <LockSimple size={10} className="shrink-0" />}
              {e.title}
            </div>
            {h > slotH && <div className="text-[10px] text-slate-400">{fmtRange(e.start, e.end)}</div>}
          </div>
        );
      })}

      {/* task blocks (draggable) */}
      {dayBlocks.map((b) => {
        const c = taskColor(b.color);
        const { top, height: h } = pos(b.start, b.end);
        return (
          <div
            key={b.id}
            data-testid={`grid-block-${b.id}`}
            draggable
            onDragStart={() => setDragItem({ type: "block", id: b.id })}
            onDragEnd={() => setDragItem(null)}
            onClick={(ev) => { ev.stopPropagation(); onBlockClick(b); }}
            className={`group absolute left-2 right-1.5 z-20 rounded-lg border px-2 py-1 overflow-hidden text-left cursor-grab active:cursor-grabbing ${c.block} ${b.done ? "opacity-55" : ""}`}
            style={{ top, height: h }}
            title={`${b.title} · ${fmtRange(b.start, b.end)}`}
          >
            <div className="flex items-center gap-1">
              <DotsSixVertical size={11} className="opacity-40 shrink-0 group-hover:opacity-70" />
              <span className={`text-[11px] font-semibold truncate ${b.done ? "line-through" : ""}`}>{b.title}</span>
              {b.done && <Check weight="bold" size={11} className="ml-auto shrink-0" />}
            </div>
            {h > slotH && <div className="text-[10px] opacity-70">{fmtRange(b.start, b.end)}</div>}
          </div>
        );
      })}

      {/* now line */}
      {isToday(day) && (() => {
        const now = new Date();
        const top = (minutesFromMidnight(now) / 30) * slotH;
        return (
          <div className="absolute left-0 right-0 z-30 pointer-events-none" style={{ top }}>
            <div className="h-px bg-rose-500 relative">
              <div className="absolute -left-1 -top-1 h-2 w-2 rounded-full bg-rose-500" />
            </div>
          </div>
        );
      })()}
    </div>
  );
}

export function TimeGrid({ days, slotH = 28, events = [], blocks = [], modes = [], dragItem, setDragItem, onSlotClick, onDropTask, onMoveBlock, onBlockClick }) {
  const scrollRef = useRef(null);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 7 * 2 * slotH; // scroll to ~7 AM
  }, [slotH]);

  return (
    <div className="flex flex-col border border-border rounded-2xl bg-white overflow-hidden">
      {/* header */}
      <div className="flex border-b border-border bg-white z-40">
        <div className="w-14 shrink-0" />
        {days.map((d) => (
          <div key={dateKey(d)} className="flex-1 min-w-[120px] text-center py-2.5 border-l border-border">
            <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">
              {d.toLocaleDateString(undefined, { weekday: "short" })}
            </div>
            <div className={`text-lg font-heading font-medium ${isToday(d) ? "text-indigo-600" : "text-slate-800"}`}>
              {d.getDate()}
            </div>
          </div>
        ))}
      </div>

      {/* body */}
      <div ref={scrollRef} className="overflow-y-auto aria-scroll" style={{ maxHeight: "calc(100vh - 250px)" }}>
        <div className="flex">
          <div className="w-14 shrink-0 relative" style={{ height: SLOTS_PER_DAY * slotH }}>
            {Array.from({ length: 24 }).map((_, h) => (
              <div key={h} className="absolute right-2 text-[10px] text-slate-400 -translate-y-1/2" style={{ top: h * 2 * slotH }}>
                {h === 0 ? "" : fmtHour(h)}
              </div>
            ))}
          </div>
          {days.map((d) => (
            <DayColumn
              key={dateKey(d)}
              day={d}
              slotH={slotH}
              events={events}
              blocks={blocks}
              modes={modes}
              dragItem={dragItem}
              setDragItem={setDragItem}
              onSlotClick={onSlotClick}
              onDropTask={onDropTask}
              onMoveBlock={onMoveBlock}
              onBlockClick={onBlockClick}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
